// ============================================================
// Seo — per-route <head> tags (Phase 34.1, reworked in Phase 46)
// ============================================================
//
// Renders nothing visible. Sets a route's title, description, canonical, robots,
// Open Graph and Twitter tags through react-helmet-async.
//
// Every route also gets these tags in its raw HTML: scripts/build-seo.mjs writes
// one file per route in src/seo/site.json (build/index.html, build/privacy.html,
// build/dashboard.html, …) for crawlers that don't run JavaScript. Those tags
// carry data-rh="true", which makes react-helmet-async replace them instead of
// adding a second copy, so a page never ends up with two canonicals or two
// descriptions. Keep the tag list below identical to headTags() in
// scripts/build-seo.mjs.
//
// Titles and descriptions live in src/seo/site.json ("routes" for public pages,
// "appRoutes" for noindex ones); pass `title`/`description` only for routes
// that aren't listed there.
//
// Requires <HelmetProvider> mounted once at the app root (see src/index.js).

import React from 'react';
import { Helmet } from 'react-helmet-async';
import site from '../seo/site.json';

function Seo({ path = '/', title, description, noindex = false }) {
  const route =
    site.routes.find((r) => r.path === path) || site.appRoutes.find((r) => r.path === path);
  const pageTitle = title || (route && route.title) || site.name;
  const pageDescription = description || (route && route.description) || site.routes[0].description;
  const canonical = `${site.url}${path}`;
  const image = site.ogImage;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* noindex pages get no canonical: Google treats noindex + canonical as conflicting hints */}
      {!noindex && <link rel="canonical" href={canonical} />}
      <meta name="robots" content={noindex ? site.robots.noindex : site.robots.index} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={site.name} />
      <meta property="og:locale" content={site.locale} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image.url} />
      <meta property="og:image:width" content={String(image.width)} />
      <meta property="og:image:height" content={String(image.height)} />
      <meta property="og:image:alt" content={image.alt} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image.url} />
      <meta name="twitter:image:alt" content={image.alt} />
    </Helmet>
  );
}

export default Seo;
